import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ProductImage } from '@/components/product/ProductImage';
import styles from './CartDrawer.module.css';

export function CartDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lines, subtotalCents, setQty, remove, count } = useCart();
  const { format } = useCurrency();
  const navigate = useNavigate();

  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const goCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${open ? styles.overlayOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        aria-hidden={!open}
      >
        <div className={styles.head}>
          <h2 className={styles.title}>Your Cart {count > 0 && `(${count})`}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close cart">
            <Icon name="close" size={20} />
          </button>
        </div>

        {lines.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyGlyph} aria-hidden="true">
              🛒
            </span>
            <p>Your cart is empty.</p>
            <Button variant="secondary" onClick={onClose}>
              Continue shopping
            </Button>
          </div>
        ) : (
          <>
            <ul className={styles.list}>
              {lines.map(({ product, qty }) => (
                <li key={product.id} className={styles.line}>
                  <Link to={`/p/${product.slug}`} className={styles.thumb} onClick={onClose}>
                    <ProductImage
                      imageKey={product.imageKey}
                      name={product.name}
                      category={product.category}
                      rarity={product.rarity}
                    />
                  </Link>
                  <div>
                    <div className={styles.lineName}>
                      <Link to={`/p/${product.slug}`} onClick={onClose}>
                        {product.name}
                      </Link>
                    </div>
                    <div className={styles.linePrice}>{format(product.priceCents)} each</div>
                    <div className={styles.qty} style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => setQty(product.id, qty - 1)}
                        disabled={qty <= 1}
                        aria-label={`Decrease ${product.name} quantity`}
                      >
                        <Icon name="minus" size={14} />
                      </button>
                      <span className={styles.qtyVal} aria-live="polite">
                        {qty}
                      </span>
                      <button
                        type="button"
                        className={styles.qtyBtn}
                        onClick={() => setQty(product.id, qty + 1)}
                        disabled={qty >= product.stock}
                        aria-label={`Increase ${product.name} quantity`}
                      >
                        <Icon name="plus" size={14} />
                      </button>
                    </div>
                  </div>
                  <div className={styles.lineRight}>
                    <button
                      type="button"
                      className={styles.remove}
                      onClick={() => remove(product.id)}
                      aria-label={`Remove ${product.name}`}
                    >
                      <Icon name="trash" size={18} />
                    </button>
                    <span className={styles.lineTotal}>{format(product.priceCents * qty)}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className={styles.foot}>
              <div className={styles.subtotal}>
                <span className={styles.subtotalLabel}>Subtotal</span>
                <span className={styles.subtotalValue}>{format(subtotalCents)}</span>
              </div>
              <Button size="lg" block onClick={goCheckout}>
                Checkout
                <Icon name="chevron-right" size={18} />
              </Button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
